import { get } from "svelte/store";
import type { StatusRow } from "./types";
import { phaseColor } from "./phases";
import type { PhaseDef } from "./phases";
import { allTaskDays, boardDayCount, resourcesForCards, statusLabel, weeksOfPlan } from "./board";
import type { DayCard } from "./board";
import { isWeekendDay, projectDayLabel, projectDayRangeLabel, projectStartDay, weekOfProjectDay } from "./projectDate";
import { subcontractorColor } from "./subcontractors";
import { tasks, det, statusRows, allowWeekendWork, projectStartDate, subcontractorColors, projectPhases, showToast } from "./stores";
import { downloadFile } from "./download";

/**
 * A shareable week-by-week PDF — the polished alternative to `window.print()`. Built
 * client-side with pdfmake so the result looks the same regardless of the viewer's
 * browser or printer, and can be filtered to one subcontractor's own scope before it's
 * handed over.
 */

// pdfmake's docDefinition is a deeply-recursive, loosely-typed tree; fighting it with
// precise types buys nothing here, since the whole thing is built in this one file.
type PdfNode = any;

/**
 * The color of a card's left accent bar — same priority the board's sticky-note
 * border uses: done/blocked status is more urgent than "whose work is this", so it
 * wins over the subcontractor color; otherwise the first subcontractor's color, so a
 * subcontractor can pick their own activities out of a busy page at a glance.
 */
function cardBarColor(card: DayCard, colors: Record<string, string>): string | null {
  if (card.done) return "#1baf7a";
  if (card.task.blocked) return "#c0392b";
  if (card.task.subcontractors.length) return subcontractorColor(colors, card.task.subcontractors[0]);
  return null;
}

/** One activity's block inside a day/phase cell — name, who's on it, and its state. */
function activityBlock(card: DayCard, colors: Record<string, string>): PdfNode {
  const t = card.task;
  const metaParts: PdfNode[] = [];
  t.subcontractors.forEach((sc) => {
    metaParts.push({ text: sc + "  ", color: subcontractorColor(colors, sc), bold: true, fontSize: 6.5 });
  });
  // Skip it when it just repeats a subcontractor tag already shown above (common for
  // single-subcontractor activities) — no point spending a card's limited space twice.
  if (t.responsibleParty && !t.subcontractors.includes(t.responsibleParty)) {
    metaParts.push({ text: t.responsibleParty + "  ", fontSize: 6.5, color: "#888" });
  }
  if (card.done) metaParts.push({ text: "done  ", color: "#1baf7a", bold: true, fontSize: 6.5 });
  else if (t.blocked) metaParts.push({ text: "blocked  ", color: "#c0392b", bold: true, fontSize: 6.5 });
  if (card.critical && !card.done) metaParts.push({ text: "critical  ", color: "#c0392b", fontSize: 6.5 });
  if (card.span > 1) metaParts.push({ text: `day ${card.index}/${card.span}`, fontSize: 6.5, color: "#888" });

  const lines: PdfNode[] = [
    { text: [{ text: t.id + "  ", bold: true, fontSize: 7.5 }, { text: t.name, fontSize: 7.5 }] },
  ];
  if (metaParts.length) lines.push({ text: metaParts, margin: [0, 1, 0, 0] });
  if (t.requiredEquipment) lines.push({ text: "Eqp: " + t.requiredEquipment, fontSize: 6.5, color: "#666" });
  if (t.requiredPersonnel) lines.push({ text: "Crew: " + t.requiredPersonnel, fontSize: 6.5, color: "#666" });
  const stack: PdfNode = { stack: lines };

  const bar = cardBarColor(card, colors);
  if (!bar) return { ...stack, margin: [0, 1, 0, 4] };

  // A borderless 2-column table rather than a real "border" — pdfmake cells have no
  // border-on-one-side concept, but a filled cell always stretches to match the
  // row's tallest neighbor, which a fixed-height canvas rect couldn't do since a
  // card's height depends on how much of its text wraps.
  return {
    table: {
      widths: [4, "*"],
      body: [[{ text: "", fillColor: bar, border: [false, false, false, false] }, { ...stack, border: [false, false, false, false] }]],
    },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: (i: number) => (i === 0 ? 0 : 5), paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
    margin: [0, 1, 0, 4],
  };
}

function phaseCell(cards: DayCard[], colors: Record<string, string>): PdfNode {
  if (!cards.length) return { text: "—", color: "#aaa", fontSize: 7, alignment: "center", margin: [0, 6, 0, 6] };
  return { stack: cards.map((c) => activityBlock(c, colors)) };
}

function dayHeaderCell(day: number, startIso: string | null, cardsThatDay: DayCard[]): PdfNode {
  const weekend = isWeekendDay(day);
  const isStart = projectStartDay(startIso) === day;
  const label = projectDayLabel(startIso, day) + (isStart ? "  ·  START" : "");
  const starting = cardsThatDay.filter((c) => c.index === 1).length;
  const running = cardsThatDay.length - starting;
  const sub = cardsThatDay.length ? `${starting} starting · ${running} running` : "—";
  return {
    stack: [
      { text: label, bold: true, fontSize: 8, color: isStart ? "#0b84b8" : weekend ? "#888" : "#111" },
      { text: sub, fontSize: 6.5, color: "#888" },
    ],
    fillColor: isStart ? "#dcecf7" : weekend ? "#f2f2f2" : "#e9edf3",
    margin: [3, 3, 3, 3],
  };
}

function weekTable(
  days: number[],
  cards: DayCard[],
  startIso: string | null,
  rows: StatusRow[],
  esOf: Record<string, number>,
  colors: Record<string, string>,
  phases: PhaseDef[]
): PdfNode {
  const perDay: DayCard[][] = days.map((d) => cards.filter((c) => c.day === d));

  const body: PdfNode[][] = [
    [
      { text: "", fillColor: "#e9edf3" },
      ...days.map((d, i) => dayHeaderCell(d, startIso, perDay[i])),
    ],
  ];

  rows.forEach((row) => {
    body.push([
      { text: row.label, bold: true, fontSize: 7.5, margin: [3, 4, 3, 4] },
      ...days.map((d) => ({
        text: statusLabel(row, row.values[d] ?? null),
        fontSize: 7.5,
        alignment: "center",
        margin: [3, 4, 3, 4],
      })),
    ]);
  });

  const equip = perDay.map((c) => resourcesForCards(c).equipment.join(", "));
  const personnel = perDay.map((c) => resourcesForCards(c).personnel.join(", "));
  body.push([
    { text: "Equipment in use", bold: true, fontSize: 7.5, margin: [3, 4, 3, 4] },
    ...equip.map((e) => ({ text: e || "—", fontSize: 7, alignment: "center", margin: [3, 4, 3, 4], color: e ? "#333" : "#aaa" })),
  ]);
  body.push([
    { text: "Personnel needed", bold: true, fontSize: 7.5, margin: [3, 4, 3, 4] },
    ...personnel.map((p) => ({ text: p || "—", fontSize: 7, alignment: "center", margin: [3, 4, 3, 4], color: p ? "#333" : "#aaa" })),
  ]);

  // A phase that's since been renamed/removed from the project's swimlanes still gets
  // its own row here, keyed by its raw name — nothing printed should silently drop
  // work that's genuinely scheduled.
  const knownPhases = new Set(phases.map((p) => p.name));
  const orphanPhases = [...new Set(cards.map((c) => c.task.phase))].filter((p) => !knownPhases.has(p));
  [...phases.map((p) => p.name), ...orphanPhases].forEach((ph) => {
    const perDayForPhase = perDay.map((c) =>
      c.filter((x) => x.task.phase === ph).sort((a, b) => (esOf[a.task.id] ?? 0) - (esOf[b.task.id] ?? 0))
    );
    body.push([
      { text: ph, bold: true, fontSize: 7.5, color: phaseColor(phases, ph), margin: [3, 5, 3, 5] },
      ...perDayForPhase.map((c) => phaseCell(c, colors)),
    ]);
  });

  return {
    // Without this, a row taller than the remaining page space gets sliced mid-card:
    // the phase label and the top of a card land on one page, the rest of the card
    // (with a now-blank label cell) spills onto the next. Keeping each row whole makes
    // it move to a fresh page instead, so every card and its phase stay together.
    table: { headerRows: 1, dontBreakRows: true, widths: [82, ...days.map(() => "*")], body },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#d5d5d5",
      vLineColor: () => "#d5d5d5",
    },
  };
}

/**
 * A colored swatch per subcontractor, repeated in the header on every page, so the
 * card-bar colors are decodable without having to flip back to page one. Only makes
 * sense for the everyone-at-once export — a single-subcontractor PDF is already all
 * one color.
 */
function subcontractorLegend(names: string[], colors: Record<string, string>): PdfNode | null {
  if (!names.length) return null;
  return {
    margin: [0, 3, 0, 0],
    columns: names.flatMap((sc) => [
      { width: 7, canvas: [{ type: "rect", x: 0, y: 1, w: 7, h: 7, r: 1, color: subcontractorColor(colors, sc) }] },
      { width: "auto", text: sc, fontSize: 7, color: "#555", margin: [3, 0, 12, 0] },
    ]),
  };
}

/** Resolves the instance regardless of how the bundler's CJS/ESM interop wrapped it. */
function resolvePdfMake(mod: PdfNode): PdfNode {
  for (const candidate of [mod, mod?.default, mod?.default?.default]) {
    if (candidate && typeof candidate.createPdf === "function") return candidate;
  }
  throw new Error("pdfmake module shape not recognized");
}

export interface WeekPlanningPdfOptions {
  /** Restrict every week to this subcontractor's own activities, equipment and personnel. */
  subcontractor?: string | null;
}

/** Loaded on demand — pdfmake (plus its bundled fonts) is only needed when exporting. */
export async function exportWeekPlanningPdf(opts: WeekPlanningPdfOptions = {}): Promise<void> {
  const result = get(det);
  if (!result.ok) {
    showToast("Fix the schedule error before exporting the week planning");
    return;
  }

  const subcontractor = opts.subcontractor?.trim() || null;
  const allCards = allTaskDays(get(tasks), result, get(allowWeekendWork));
  const cards = subcontractor ? allCards.filter((c) => c.task.subcontractors.includes(subcontractor)) : allCards;
  if (subcontractor && cards.length === 0) {
    showToast(`No activities found for ${subcontractor}`);
    return;
  }

  const startIso = get(projectStartDate);
  const rows = get(statusRows);
  const colors = get(subcontractorColors);
  const phases = get(projectPhases);
  // Week numbers stay anchored to the whole project's plan (matching the Plan/Week
  // board elsewhere) even when filtered — only weeks with no matching card are
  // dropped, so a subcontractor isn't handed a stack of blank pages either side of
  // their own work.
  const maxDay = boardDayCount(allCards, result.projectEnd);
  const weeks = weeksOfPlan(maxDay)
    .filter((days) => !subcontractor || days.some((d) => cards.some((c) => c.day === d)));

  const content: PdfNode[] = [];
  weeks.forEach((days, wi) => {
    content.push({ text: `Week ${weekOfProjectDay(days[0])}`, style: "weekTitle", pageBreak: wi > 0 ? "before" : undefined });
    content.push({ text: projectDayRangeLabel(startIso, days), style: "weekRange" });
    content.push(weekTable(days, cards, startIso, rows, result.ES, colors, phases));
  });

  const generated = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const titleSuffix = subcontractor ? ` — ${subcontractor}` : "";
  // Filtered exports are already all one subcontractor's color, so the legend would
  // just be a single swatch repeating the title — only worth it on the combined view.
  const legend = subcontractor ? null : subcontractorLegend([...new Set(cards.flatMap((c) => c.task.subcontractors))].sort(), colors);

  const docDefinition: PdfNode = {
    pageSize: "A3",
    pageOrientation: "landscape",
    pageMargins: [24, legend ? 58 : 46, 24, 34],
    header: {
      margin: [24, 16, 24, 0],
      stack: [
        {
          columns: [
            { text: `Crane changeover — week planning${titleSuffix}`, fontSize: 9, bold: true, color: "#333" },
            { text: `Generated ${generated}`, fontSize: 8, color: "#888", alignment: "right" },
          ],
        },
        ...(legend ? [legend] : []),
      ],
    },
    footer: (currentPage: number, pageCount: number) => ({
      margin: [24, 0, 24, 12],
      columns: [
        { text: "Deterministic (M) schedule — for planning use, not a contractual document", fontSize: 7, color: "#999" },
        { text: `Page ${currentPage} of ${pageCount}`, fontSize: 7, color: "#999", alignment: "right" },
      ],
    }),
    content,
    styles: {
      weekTitle: { fontSize: 15, bold: true, margin: [0, 0, 0, 1] },
      weekRange: { fontSize: 10, color: "#666", margin: [0, 0, 0, 8] },
    },
    defaultStyle: { fontSize: 8 },
  };

  const pdfMakeMod = await import("pdfmake/build/pdfmake.js");
  const vfsMod = await import("pdfmake/build/vfs_fonts.js");
  const pdfMake = resolvePdfMake(pdfMakeMod);
  pdfMake.addVirtualFileSystem((vfsMod as PdfNode).default ?? vfsMod);

  const blob: Blob = await pdfMake.createPdf(docDefinition).getBlob();
  const slug = subcontractor ? "-" + subcontractor.toLowerCase().replace(/\s+/g, "-") : "";
  await downloadFile(`crane-week-planning${slug}.pdf`, blob, "application/pdf");
}
